import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAdminAuth, PlatformRole } from '@/context/AdminAuthContext';
import type { components } from '@/api/schema';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Shield, Plus, Pencil, Trash2, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@/lib/api-error';
import { intlLocale } from './adminLocale';

const PLATFORM_ROLES = ['platform_owner', 'platform_director', 'support_admin', 'finance_admin', 'auditor'] as const;

const roleBadgeColors: Record<string, string> = {
  platform_owner: 'bg-primary/10 text-primary border-primary/20',
  platform_director: 'bg-primary/10 text-primary border-primary/20',
  support_admin: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  finance_admin: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  auditor: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
};

type PlatformUser = components['schemas']['PlatformUserResource'];

export default function AdminPlatformUsers() {
  const { t } = useTranslation();
  const roleLabel = (role: string) => t(`admin_roles.${role}.label`, { defaultValue: role });
  const roleDescription = (role: string) => t(`admin_roles.${role}.description`, { defaultValue: '' });
  const { admin } = useAdminAuth();
  const isPlatformOwner = admin?.role === 'platform_owner';
  const queryClient = useQueryClient();

  const { data, isLoading: loading } = useQuery({ queryKey: ['/admin/platform-users'] });
  const users: PlatformUser[] = data?.data ?? [];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['/admin/platform-users'] });

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addFullName, setAddFullName] = useState('');
  const [addPassword, setAddPassword] = useState('');
  const [addRole, setAddRole] = useState<string>('support_admin');
  const [addSaving, setAddSaving] = useState(false);

  // Role change dialog
  const [roleDialog, setRoleDialog] = useState(false);
  const [roleUser, setRoleUser] = useState<PlatformUser | null>(null);
  const [newRole, setNewRole] = useState('');
  const [roleSaving, setRoleSaving] = useState(false);

  // Remove confirmation
  const [removeDialog, setRemoveDialog] = useState(false);
  const [removeUser, setRemoveUser] = useState<PlatformUser | null>(null);
  const [removeSaving, setRemoveSaving] = useState(false);

  const handleAdd = async () => {
    if (!addEmail.trim()) { toast.error(t('adm_platformUsers.enterEmail')); return; }
    // An existing account keeps its password (FR-96); the API asks for one only for a new account.
    if (addPassword && addPassword.length < 8) { toast.error(t('adm_platformUsers.passwordMin')); return; }
    setAddSaving(true);
    try {
      const { error } = await apiClient.POST('/admin/platform-users', {
        body: { name: addFullName.trim(), email: addEmail.trim(), password: addPassword || undefined, role: addRole as never },
      });
      if (error) throw error;
      toast.success(t('adm_platformUsers.added'));
      setAddOpen(false);
      setAddEmail('');
      setAddFullName('');
      setAddPassword('');
      setAddRole('support_admin');
      invalidate();
    } catch (err) {
      toast.error(apiErrorMessage(err, t('adm_common.errorGeneric')));
    } finally { setAddSaving(false); }
  };

  const handleRoleChange = async () => {
    if (!roleUser || !newRole) return;
    setRoleSaving(true);
    try {
      // One request replaces the role atomically on the server (FR-96).
      const { error } = await apiClient.PATCH('/admin/platform-users/{user}/role', {
        params: { path: { user: roleUser.id } },
        body: { role: newRole as never },
      });
      if (error) throw error;
      toast.success(t('adm_platformUsers.roleUpdated'));
      setRoleDialog(false);
      invalidate();
    } catch (err) {
      toast.error(apiErrorMessage(err, t('adm_common.errorGeneric')));
    } finally { setRoleSaving(false); }
  };

  const handleRemove = async () => {
    if (!removeUser) return;
    setRemoveSaving(true);
    try {
      for (const role of removeUser.roles) {
        const { error } = await apiClient.DELETE('/admin/platform-users/{user}/role/{role}', {
          params: { path: { user: removeUser.id, role: role as never } },
        });
        if (error) throw error;
      }
      toast.success(t('adm_platformUsers.removed'));
      setRemoveDialog(false);
      invalidate();
    } catch (err) {
      toast.error(apiErrorMessage(err, t('adm_common.errorGeneric')));
    } finally { setRemoveSaving(false); }
  };

  if (!isPlatformOwner) {
    return (
      <div className="p-6">
        <div className="text-center py-12 text-muted-foreground">
          <Shield className="mx-auto h-10 w-10 opacity-30 mb-2" />
          <p>{t('adm_platformUsers.ownerOnly')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('adm_platformUsers.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('adm_platformUsers.subtitle')}</p>
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus size={16} className="mr-1" /> {t('adm_platformUsers.newAdmin')}
        </Button>
      </div>

      {/* Role descriptions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {PLATFORM_ROLES.map(key => (
          <div key={key} className="rounded-lg border border-border p-3 space-y-1">
            <Badge variant="outline" className={roleBadgeColors[key]}>{roleLabel(key)}</Badge>
            <p className="text-xs text-muted-foreground">{roleDescription(key)}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="text-muted-foreground py-8">{t('adm_common.loading')}</div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('adm_platformUsers.name')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">Email</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('adm_platformUsers.role')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('adm_platformUsers.joined')}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-muted-foreground">
                    <Users className="mx-auto mb-2 h-8 w-8 opacity-30" />
                    {t('adm_platformUsers.empty')}
                  </td>
                </tr>
              ) : (
                users.map(u => {
                  const isSelf = u.id === admin?.id;
                  const primaryRole = u.roles[0] ?? '';
                  return (
                    <tr key={u.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="p-3 text-sm font-medium text-foreground">
                        {u.name}
                        {isSelf && <span className="ml-1 text-xs text-muted-foreground">({t('adm_platformUsers.you')})</span>}
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">{u.email}</td>
                      <td className="p-3">
                        {u.roles.map(role => (
                          <Badge key={role} variant="outline" className={`${roleBadgeColors[role] || ''} mr-1`}>
                            {roleLabel(role)}
                          </Badge>
                        ))}
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {u.added_at ? new Date(u.added_at).toLocaleDateString(intlLocale()) : '—'}
                      </td>
                      <td className="p-3">
                        {!isSelf && (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => { setRoleUser(u); setNewRole(primaryRole); setRoleDialog(true); }}
                              className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground"
                              title={t('adm_platformUsers.changeRole')}
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => { setRemoveUser(u); setRemoveDialog(true); }}
                              className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive"
                              title={t('adm_platformUsers.delete')}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Admin Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('adm_platformUsers.addDialogTitle')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{t('adm_platformUsers.fullName')}</Label>
              <Input value={addFullName} onChange={e => setAddFullName(e.target.value)} placeholder={t('adm_platformUsers.fullNamePlaceholder')} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email *</Label>
              <Input type="email" value={addEmail} onChange={e => setAddEmail(e.target.value)} placeholder="admin@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('adm_platformUsers.password')}</Label>
              <Input type="password" value={addPassword} onChange={e => setAddPassword(e.target.value)} placeholder={t('adm_platformUsers.passwordPlaceholder')} />
              <p className="text-xs text-muted-foreground">{t('platform_users.password_hint')}</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('adm_platformUsers.role')}</Label>
              <Select value={addRole} onValueChange={setAddRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORM_ROLES.map(key => (
                    <SelectItem key={key} value={key}>{roleLabel(key)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{roleDescription(addRole)}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>{t('adm_common.cancel')}</Button>
            <Button onClick={handleAdd} disabled={addSaving}>
              {addSaving ? <><Loader2 size={14} className="animate-spin mr-1" /> {t('adm_common.saving')}</> : t('adm_platformUsers.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Change Dialog */}
      <Dialog open={roleDialog} onOpenChange={setRoleDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('adm_platformUsers.changeRoleTitle')}: {roleUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>{t('adm_platformUsers.newRole')}</Label>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLATFORM_ROLES.map(key => (
                  <SelectItem key={key} value={key}>{roleLabel(key)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{roleDescription(newRole)}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialog(false)}>{t('adm_common.cancel')}</Button>
            <Button onClick={handleRoleChange} disabled={roleSaving}>
              {roleSaving ? <><Loader2 size={14} className="animate-spin mr-1" /> {t('adm_common.saving')}</> : t('adm_common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation */}
      <AlertDialog open={removeDialog} onOpenChange={setRemoveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('adm_platformUsers.removeTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('adm_platformUsers.removeConfirm', { name: removeUser?.name, email: removeUser?.email })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeSaving}>{t('adm_common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove} disabled={removeSaving} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {removeSaving ? t('adm_platformUsers.deleting') : t('adm_platformUsers.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
